import type {
  RouteSegment,
  SegmentStopInput,
  SegmentStopSuggestion,
} from '../../../../types/surveillanceRoute';
import {
  STOP_EDGE_MS,
  stopShape,
  tryPlanSegmentSplit,
  visibleStopMinutes,
  type PlannedPiece,
  type SplitSource,
  type StopRequest,
  type StopShape,
} from '../../../../utils/segmentStopPlan';
import { validateSegmentCorrection } from '../../../../utils/segmentCorrection';
import { parsePk, pkFormatter } from '../../../../utils/transform';
import { formatSegmentKm } from '../../../../utils/surveillanceRoute';

/**
 * Lógica PURA del modal «Registrar parada» (spec 2026-10-06 § 7.2).
 *
 * El modal edita una lista de borradores de parada; esto los convierte en el
 * plan (espejo del back), los valida contra los tramos vecinos con las MISMAS
 * reglas de «Corregir» (`validateSegmentCorrection`) y arma la vista previa.
 * El servidor revalida todo: aquí solo se adelanta el error para que se vea
 * antes de enviar.
 */

export type StopDraft = {
  key: string;
  enabled: boolean;
  /** Vino del GPS (no la escribió nadie a mano). */
  fromGps: boolean;
  startDate: string;
  endDate: string;
  /** Formato `K + MMM`; vacío si el GPS cayó fuera de la vía. */
  pk: string;
  roadId: string | null;
  latitude: number | null;
  longitude: number | null;
  pauseCause: string;
  pauseCauseDetail: string;
  directionAfter: string | null;
  minutes: number | null;
  onRoad: boolean;
  /** La OT que la regla de la alerta ligaría (si la parada es un aviso). */
  candidateRoid: string | null;
  /** Su id y su tipo: «Así quedará» la enseña en «OT sala» (29-oct). */
  candidateId: string | null;
  candidateType: string | null;
  candidatesCount: number;
  /** Respecto al tramo entero (la del plan puede cambiar con varias paradas). */
  shape: StopShape | 'covers';
};

export type PreviewRow =
  | {
      kind: 'piece';
      number: number;
      from: string;
      to: string;
      start: string;
      end: string;
      km: string;
      pauseCause: string | null;
      pauseCauseDetail: string | null;
      isOriginal: boolean;
    }
  | { kind: 'stop'; minutes: number };

export type DraftPlan = {
  /** Clave i18n del primer error, o null si se puede guardar. */
  error: string | null;
  /**
   * La parada a la que pertenece el error, para señalarla en su tarjeta (con
   * varias marcadas, un mensaje suelto al pie no dice cuál tocar). Null si el
   * error es del conjunto.
   */
  errorDraftKey: string | null;
  rows: PreviewRow[];
  /**
   * «Así quedará» (14-oct): las piezas como tramos, para pintarlas con la
   * propia tabla de Tramos en solo lectura. La nueva lleva la causa de la
   * parada y ningún vínculo ni marca del original; el original conserva los
   * suyos.
   */
  pieces: RouteSegment[];
  /** Nº de la primera pieza en la tabla de Tramos (las demás, seguidas). */
  firstNumber: number;
  /** Recorta el inicio del primer tramo o el fin del último: cambian horas. */
  shortensShift: boolean;
  /**
   * 29-oct: solo falta la causa de alguna parada. «Así quedará» se enseña igual
   * (con `error` puesto, no se puede guardar) y `missingCauseIds` dice qué
   * piezas se quedan sin causa.
   */
  causeMissing: boolean;
  missingCauseIds: string[];
  /**
   * 29-oct: «Así quedará» como un trozo de la tabla de Tramos: el tramo de
   * antes (si hay), las piezas y el de después (si hay), los vecinos tal cual.
   */
  previewSegments: RouteSegment[];
  /** Nº de la primera fila de `previewSegments` en la tabla de Tramos. */
  previewFirstNumber: number;
  /** Qué le pasa a cada fila de la vista previa, por id de tramo. */
  rowNotes: Record<string, PreviewRowNote>;
};

/**
 * Qué es cada fila de «Así quedará». 30-oct (el usuario: «no entiendo entre
 * parada nueva y nuevo»): en árbol. `parent` es el tramo tal como está hoy;
 * de él cuelgan las piezas (`depth: 1`) y, de cada pieza, la parada que la
 * cierra (`depth: 2`), porque la causa de un tramo es la de la parada en la que
 * acaba. La parada pegada al inicio no cierra ninguna pieza: cuelga del padre.
 */
export type PreviewRowNote = {
  kind: 'context' | 'parent' | 'piece' | 'stop';
  /** Nº que tenía en la tabla antes de registrar (si cambia, se avisa). */
  wasNumber?: number;
  /** El padre enseña su Nº de hoy, que no cuenta para los de debajo. */
  number?: number;
  depth?: 1 | 2;
  /** La última pieza: la línea del árbol acaba en ella. */
  last?: boolean;
  /** Parada de nivel 2: la línea de las piezas sigue por debajo de ella. */
  through?: boolean;
  /** Parada pegada a un borde del tramo. */
  edge?: 'start' | 'end';
  /**
   * Nº del tramo cuya pausa es la parada de un borde: el de antes (al inicio)
   * o la última pieza (al final). Nunca el de después.
   */
  pauseOf?: number;
  /** Lo que estuvo parado según la tabla de arriba (sin los minutos en marcha). */
  stoppedMinutes?: number;
  /**
   * Parada en un borde del TURNO (inicio del primer tramo o fin del último):
   * no alarga ninguna pausa, el turno empieza o acaba con ella.
   */
  shiftEdge?: boolean;
  /**
   * Minutos en marcha que pasan a la pausa: los que anduvo antes de parar
   * (al inicio) o después de arrancar otra vez (al final). Por eso la fila
   * dura más que la parada de arriba.
   */
  extraMinutes?: number;
};

const CAUSE_REQUIRED = 'errors:backend.SR_STOP_CAUSE_REQUIRED';
/** Causa provisional para calcular el plan mientras falta la de verdad. */
const PLACEHOLDER_CAUSE = 'basic';

const shapeOf = (segment: RouteSegment, start: string, end: string) =>
  stopShape(new Date(segment.startDate), new Date(segment.endDate as string), {
    startDate: new Date(start),
    endDate: new Date(end),
  });

/** La «Duración» de la tabla: al minuto que se ve (QA 15-oct, T1.2+a). */
const minutesOf = (start: string, end: string): number =>
  visibleStopMinutes(new Date(start), new Date(end));

const MINUTE_MS = 60_000;
const minuteOf = (ms: number) => Math.floor(ms / MINUTE_MS);

/**
 * Una hora tecleada en el MISMO minuto que el inicio (o el fin) del tramo pero
 * por fuera, por segundos que no se ven, se queda en el borde exacto. El tramo
 * empieza a las 13:23:49 y la tabla dice 13:23: la llegada «13:23» tecleada
 * (13:23:00) caía fuera, y la del GPS cambiada a 13:23 (13:23:56) no (QA
 * 15-oct, T1.x+c). Fuera de ese minuto sigue fuera: el plan lo dirá.
 */
const snapToSegmentEdge = (segment: RouteSegment, iso: string): string => {
  const t = new Date(iso).getTime();
  const start = new Date(segment.startDate).getTime();
  const end = new Date(segment.endDate as string).getTime();
  if (t < start && minuteOf(t) === minuteOf(start)) {
    return new Date(start).toISOString();
  }
  if (t > end && minuteOf(t) === minuteOf(end)) {
    return new Date(end).toISOString();
  }
  return iso;
};

/**
 * La causa y la descripción con las que nace una parada según su forma.
 * Pegada al final: la patrulla cerró DESPUÉS de trabajar y lo que escribió al
 * cerrar ES la descripción de la parada. En medio: si hay OT, es un aviso.
 */
const causeDefaults = (
  segment: RouteSegment,
  shape: StopDraft['shape'],
  candidatesCount: number
): Pick<StopDraft, 'pauseCause' | 'pauseCauseDetail'> => ({
  pauseCause:
    shape === 'end'
      ? (segment.pauseCause ?? '')
      : shape === 'middle' && candidatesCount > 0
        ? 'external-call'
        : '',
  pauseCauseDetail: shape === 'end' ? (segment.pauseCauseDetail ?? '') : '',
});

/**
 * La causa con la que nace la parada en su forma actual (la del cierre del
 * tramo si está al final). El desplegable la ofrece siempre, aunque el rol no
 * pueda elegirla (15-oct, `selectableSegmentCauses`): no crea nada nuevo.
 */
export const preloadedStopCause = (
  segment: RouteSegment,
  draft: Pick<StopDraft, 'shape' | 'candidatesCount'>
): string =>
  causeDefaults(segment, draft.shape, draft.candidatesCount).pauseCause;

/**
 * Cambiar la llegada o la salida puede cambiar la FORMA de la parada (una a
 * mano llevada al final, una «al final» adelantada al medio). La forma manda
 * en lo que se precarga y en lo que se enseña: se recalcula, y la causa y la
 * descripción pasan a las de la forma nueva solo si nadie las había tocado.
 * Sin esto, una parada llevada al final mandaba una descripción vacía que
 * borraba la del cierre.
 */
export function retimeDraft(
  segment: RouteSegment,
  draft: StopDraft,
  patch: { startDate?: string; endDate?: string },
  /**
   * `canChooseActionCause: false` (revisión M4, 15-oct): sin poder crear la
   * básica en el recorrido (sala), una «Actuación» que ya no sea la precargada
   * de la forma nueva se vacía. Se quedaba si se había tocado la descripción,
   * y al guardar creaba una pieza NUEVA pendiente de una básica que nadie
   * puede crear. Vacía, el plan exige causa y no deja guardar.
   */
  options: { canChooseActionCause?: boolean } = {}
): StopDraft {
  const startDate = snapToSegmentEdge(
    segment,
    patch.startDate ?? draft.startDate
  );
  const endDate = snapToSegmentEdge(segment, patch.endDate ?? draft.endDate);
  const shape = shapeOf(segment, startDate, endDate);
  const next: StopDraft = {
    ...draft,
    startDate,
    endDate,
    shape,
    minutes: minutesOf(startDate, endDate),
  };
  if (shape === draft.shape) return next;

  const before = causeDefaults(segment, draft.shape, draft.candidatesCount);
  const untouched =
    draft.pauseCause === before.pauseCause &&
    draft.pauseCauseDetail === before.pauseCauseDetail;
  const reshaped: StopDraft = {
    ...next,
    ...(untouched && causeDefaults(segment, shape, draft.candidatesCount)),
    // El sentido después solo existe en medio: fuera de ahí ni se ve.
    directionAfter: shape === 'middle' ? draft.directionAfter : null,
  };
  const actionNoLongerAllowed =
    options.canChooseActionCause === false &&
    reshaped.pauseCause === 'action' &&
    preloadedStopCause(segment, reshaped) !== 'action';
  return actionNoLongerAllowed ? { ...reshaped, pauseCause: '' } : reshaped;
}

/**
 * Una sugerencia del GPS → borrador. Se marca de entrada solo si hay
 * EXACTAMENTE una OT candidata: es el caso que el usuario viene a arreglar
 * (un aviso que no se registró) y el que no necesita decisión.
 */
export function suggestionToDraft(
  suggestion: SegmentStopSuggestion,
  segment: RouteSegment,
  index: number
): StopDraft {
  const shape = shapeOf(segment, suggestion.startDate, suggestion.endDate);
  return {
    key: `gps-${index}`,
    enabled: suggestion.candidatesCount === 1,
    fromGps: true,
    startDate: suggestion.startDate,
    endDate: suggestion.endDate,
    pk:
      suggestion.pkKm != null
        ? pkFormatter(suggestion.pkKm, suggestion.pkMeter ?? 0)
        : '',
    roadId: suggestion.roadId,
    latitude: suggestion.latitude,
    longitude: suggestion.longitude,
    ...causeDefaults(segment, shape, suggestion.candidatesCount),
    directionAfter: null,
    // La que se ve (de la hora a la hora), la misma que tras editarla: el
    // GPS cuenta segundos que la tabla no enseña.
    minutes: minutesOf(suggestion.startDate, suggestion.endDate),
    onRoad: suggestion.onRoad,
    candidateRoid: suggestion.candidateOrder?.roid ?? null,
    candidateId: suggestion.candidateOrder?.id ?? null,
    candidateType: suggestion.candidateOrder?.type ?? null,
    candidatesCount: suggestion.candidatesCount,
    shape,
  };
}

/**
 * Las horas de una parada escrita a mano, o `null` si en el tramo no cabe
 * ninguna. Con las horas al minuto (como se escriben), una parada dura al
 * menos 1 min y tiene que dejar `STOP_EDGE_MS` de tramo a un lado: si toca
 * los dos bordes «ocupa todo el tramo» (SR_STOP_COVERS_SEGMENT). Por eso un
 * tramo de menos de 3 min no se puede partir (revisión del 22-oct: en uno de
 * 1 min proponía 0 min y luego no había forma de arreglarla).
 *
 * Por orden de preferencia:
 * - en medio: en el primer tercio, de 10 min o menos para que quepa (QA
 *   15-oct, T1.x+g: en un tramo de 6 min proponía 10 que se salían), sin
 *   quedar pegada al final, que la convertiría en «al final» sin pedirlo;
 * - al final: el último minuto, hasta el cierre;
 * - al inicio: el primer minuto entero.
 */
export function manualStopTimes(
  start: number,
  end: number
): { from: number; to: number } | null {
  const fromMs = Math.max(
    Math.round((start + (end - start) / 3) / MINUTE_MS) * MINUTE_MS,
    Math.ceil(start / MINUTE_MS) * MINUTE_MS
  );
  // Lo más tarde que puede acabar sin quedar pegada al final del tramo.
  const middleEnd = Math.floor((end - STOP_EDGE_MS) / MINUTE_MS) * MINUTE_MS;
  if (middleEnd - fromMs >= MINUTE_MS) {
    return { from: fromMs, to: Math.min(fromMs + 10 * MINUTE_MS, middleEnd) };
  }
  const lastMinute = Math.floor(end / MINUTE_MS) * MINUTE_MS - MINUTE_MS;
  if (lastMinute - start >= STOP_EDGE_MS) {
    return { from: lastMinute, to: end };
  }
  const firstMinute = Math.ceil(start / MINUTE_MS) * MINUTE_MS;
  if (middleEnd - firstMinute >= MINUTE_MS) {
    return { from: firstMinute, to: firstMinute + MINUTE_MS };
  }
  return null;
}

/** Si en el tramo cabe alguna parada (ver `manualStopTimes`). */
export const canSegmentTakeStop = (segment: RouteSegment): boolean =>
  !!segment.endDate &&
  manualStopTimes(
    new Date(segment.startDate).getTime(),
    new Date(segment.endDate).getTime()
  ) !== null;

/**
 * Las horas de una parada a mano cuando ya hay otras MARCADAS (29-oct): en el
 * hueco libre más grande del tramo, en su medio, de hasta 10 min y dejando
 * `STOP_EDGE_MS` de tramo a cada lado —entre dos paradas tiene que existir una
 * pieza, y dos que se tocan no la dejan—. `null` si en ningún hueco cabe 1 min.
 */
const manualTimesAmong = (
  start: number,
  end: number,
  marked: StopDraft[]
): { from: number; to: number } | null => {
  const stops = marked
    .map((d) => ({
      from: new Date(d.startDate).getTime(),
      to: new Date(d.endDate).getTime(),
    }))
    .sort((a, b) => a.from - b.from);
  // Huecos entre paradas (y los bordes del tramo), con su ventana útil.
  const gaps: { a: number; b: number; lo: number; hi: number }[] = [];
  let cursor = start;
  for (const stop of [...stops, { from: end, to: end, edge: true }]) {
    const isEdge = 'edge' in stop;
    if (stop.from > cursor) {
      gaps.push({
        a: cursor,
        b: stop.from,
        lo: cursor + STOP_EDGE_MS,
        hi: stop.from - STOP_EDGE_MS,
      });
    }
    if (!isEdge) {
      cursor = Math.max(cursor, stop.to);
    }
  }
  const best = gaps
    .map((g) => {
      const fromMin = Math.ceil(g.lo / MINUTE_MS);
      const avail = Math.floor(g.hi / MINUTE_MS) - fromMin;
      return { ...g, fromMin, avail };
    })
    .filter((g) => g.avail >= 1)
    .sort((x, y) => y.b - y.a - (x.b - x.a))[0];
  if (!best) return null;
  const durMin = Math.min(10, best.avail);
  const startMin = best.fromMin + Math.floor((best.avail - durMin) / 2);
  return { from: startMin * MINUTE_MS, to: (startMin + durMin) * MINUTE_MS };
};

/**
 * Una parada escrita a mano (`null` si en el tramo no cabe ninguna). Con otras
 * marcadas va a un hueco libre: antes caía siempre a un tercio del tramo, o
 * sea encima de la que ya estuviera allí.
 */
export function manualDraft(
  segment: RouteSegment,
  index: number,
  others: StopDraft[] = []
): StopDraft | null {
  const marked = others.filter((d) => d.enabled);
  const start = new Date(segment.startDate).getTime();
  const times = segment.endDate
    ? marked.length > 0
      ? manualTimesAmong(start, new Date(segment.endDate).getTime(), marked)
      : manualStopTimes(start, new Date(segment.endDate).getTime())
    : null;
  if (!times) return null;
  const from = new Date(times.from).toISOString();
  const to = new Date(times.to).toISOString();
  const shape = shapeOf(segment, from, to);
  return {
    key: `manual-${index}`,
    enabled: true,
    fromGps: false,
    startDate: from,
    endDate: to,
    pk: '',
    roadId: null,
    latitude: null,
    longitude: null,
    // La de su forma, como al moverla (una «al final» conserva el cierre).
    ...causeDefaults(segment, shape, 0),
    directionAfter: null,
    minutes: minutesOf(from, to),
    onRoad: true,
    candidateRoid: null,
    candidateId: null,
    candidateType: null,
    candidatesCount: 0,
    shape,
  };
}

/**
 * Lo que el PK no arregla va antes que pedirlo (revisión del 22-oct): con una
 * parada de 0 min y sin PK se pedía el PK y, al escribirlo, salía que tenía
 * que durar 1 minuto. Son las reglas de horas de `planSegmentSplit`, en su
 * mismo orden; las que dependen del PK o de cómo se reparten las paradas
 * siguen saliendo del plan.
 */
const timeError = (
  segment: RouteSegment,
  enabled: StopDraft[]
): { error: string; key: string } | null => {
  const segStart = new Date(segment.startDate).getTime();
  const segEnd = new Date(segment.endDate as string).getTime();
  const sorted = [...enabled].sort(
    (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
  );
  for (const draft of sorted) {
    const start = new Date(draft.startDate).getTime();
    const end = new Date(draft.endDate).getTime();
    if (end < start) {
      return { error: 'errors:backend.SR_STOP_TIME_INVALID_RANGE', key: draft.key };
    }
    if (minutesOf(draft.startDate, draft.endDate) < 1) {
      return { error: 'errors:backend.SR_STOP_TOO_SHORT', key: draft.key };
    }
    if (start < segStart || end > segEnd) {
      return { error: 'errors:backend.SR_STOP_OUTSIDE_SEGMENT', key: draft.key };
    }
  }
  return null;
};

const toSource = (segment: RouteSegment): SplitSource => ({
  startDate: new Date(segment.startDate),
  endDate: new Date(segment.endDate as string),
  pkInit: { km: segment.pkInitKm, meter: segment.pkInitMeter },
  pkEnd: {
    km: segment.pkEndKm ?? segment.pkInitKm,
    meter: segment.pkEndMeter ?? segment.pkInitMeter,
  },
  direction: segment.direction,
  directionName: segment.directionName ?? null,
  roadId: segment.roadId ?? null,
  roadStretches: segment.roadStretches ?? [],
  pauseCause: segment.pauseCause ?? null,
  pauseCauseDetail: segment.pauseCauseDetail ?? null,
  initLatitude: segment.initLatitude ?? null,
  initLongitude: segment.initLongitude ?? null,
  endLatitude: segment.endLatitude ?? null,
  endLongitude: segment.endLongitude ?? null,
});

const toRequest = (draft: StopDraft): StopRequest | null => {
  const pk = parsePk(draft.pk);
  if (!pk) return null;
  return {
    startDate: new Date(draft.startDate),
    endDate: new Date(draft.endDate),
    pk,
    roadId: draft.roadId,
    latitude: draft.latitude,
    longitude: draft.longitude,
    pauseCause: draft.pauseCause || null,
    pauseCauseDetail: draft.pauseCauseDetail,
    directionAfter: draft.directionAfter,
  };
};

const pieceAsSegment = (
  piece: PlannedPiece,
  id: string,
  base: RouteSegment
): RouteSegment => ({
  ...base,
  id,
  startDate: piece.startDate.toISOString(),
  endDate: piece.endDate.toISOString(),
  pkInitKm: piece.pkInit.km,
  pkInitMeter: piece.pkInit.meter,
  pkEndKm: piece.pkEnd.km,
  pkEndMeter: piece.pkEnd.meter,
  direction: piece.direction,
  roadId: piece.roadId,
  roadStretches: piece.roadStretches,
});

const hm = (d: Date) =>
  d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

/**
 * Una parada marcada como fila de la vista previa: un «tramo» de un solo punto
 * (mismo PK al empezar y al acabar). Sin causa —la lleva la pieza que cierra,
 * 30-oct—, sin vínculos ni marcas, sin sentido.
 */
const rowKindIsStop = (id: string) => id.startsWith('stop-');

const stopAsSegment = (draft: StopDraft, base: RouteSegment): RouteSegment => {
  const pk = parsePk(draft.pk) as { km: number; meter: number };
  return {
    ...base,
    id: `stop-${draft.key}`,
    startDate: new Date(draft.startDate).toISOString(),
    endDate: new Date(draft.endDate).toISOString(),
    pkInitKm: pk.km,
    pkInitMeter: pk.meter,
    pkEndKm: pk.km,
    pkEndMeter: pk.meter,
    direction: null as unknown as string,
    directionName: undefined,
    roadId: draft.roadId ?? base.roadId,
    roadStretches: [],
    pauseCause: null,
    pauseCauseDetail: null,
    linkedBasicActionOrder: null,
    linkedExternalOrder: null,
    stopSplit: null,
    transfer: null,
    pendingActionLink: false,
    pendingExternalLink: false,
  };
};

/**
 * De dos paradas que se pisan o se tocan, la de DESPUÉS (por hora de inicio):
 * es en la que se enseña el aviso. Sin ninguna pareja así, nada.
 */
const touchingCulprit = (enabled: StopDraft[]): StopDraft | undefined => {
  const sorted = [...enabled].sort(
    (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
  );
  return sorted.find(
    (d, i) =>
      i > 0 &&
      new Date(d.startDate).getTime() <=
        Math.max(
          ...sorted.slice(0, i).map((p) => new Date(p.endDate).getTime())
        )
  );
};

export function planDrafts(
  segment: RouteSegment,
  segments: RouteSegment[],
  drafts: StopDraft[]
): DraftPlan {
  const empty: DraftPlan = {
    error: null,
    errorDraftKey: null,
    rows: [],
    pieces: [],
    firstNumber: 1,
    shortensShift: false,
    causeMissing: false,
    missingCauseIds: [],
    previewSegments: [],
    previewFirstNumber: 1,
    rowNotes: {},
  };
  const enabled = drafts.filter((d) => d.enabled);
  const badTime = timeError(segment, enabled);
  if (badTime) {
    return { ...empty, error: badTime.error, errorDraftKey: badTime.key };
  }
  const requests = enabled.map(toRequest);
  const badPk = enabled.find((_, i) => requests[i] === null);
  if (badPk) {
    return {
      ...empty,
      error: 'pages:surveillance.registerStop.pkRequired',
      errorDraftKey: badPk.key,
    };
  }
  let result = tryPlanSegmentSplit(toSource(segment), requests as StopRequest[]);
  // Si solo falta la causa, el plan se calcula con una provisional para que
  // «Así quedará» se vea; esas piezas salen luego sin causa.
  const missingCause =
    'error' in result && result.error === CAUSE_REQUIRED
      ? enabled.filter((d) => d.shape === 'middle' && !d.pauseCause)
      : [];
  if (missingCause.length > 0) {
    result = tryPlanSegmentSplit(
      toSource(segment),
      (requests as StopRequest[]).map((r, i) =>
        missingCause.includes(enabled[i])
          ? { ...r, pauseCause: PLACEHOLDER_CAUSE }
          : r
      )
    );
  }
  if ('error' in result) {
    // La causa que falta sí sabemos de quién es: la primera parada en medio
    // que no la tiene. Y la que no llega al minuto, la que dura menos.
    const culprit =
      result.error === 'errors:backend.SR_STOP_CAUSE_REQUIRED'
        ? enabled.find((d) => d.shape === 'middle' && !d.pauseCause)
        : result.error === 'errors:backend.SR_STOP_TOO_SHORT'
          ? enabled.find((d) => minutesOf(d.startDate, d.endDate) < 1)
          : result.error === 'errors:backend.SR_STOP_OVERLAP' ||
              result.error === 'errors:backend.SR_STOP_COVERS_SEGMENT'
            ? touchingCulprit(enabled)
            : undefined;
    return {
      ...empty,
      error: result.error,
      errorDraftKey: culprit?.key ?? null,
    };
  }
  const { plan } = result;
  // Mismo criterio que el servidor: un cierre forzado (sin PK final) solo se
  // parte con la parada del final, la que dice dónde acabó.
  if (segment.pkEndKm == null && !plan.endMoved) {
    return { ...empty, error: 'errors:backend.SR_STOP_END_REQUIRED' };
  }

  // Las piezas sustituyen al tramo en la lista: cada una se valida contra sus
  // vecinas reales con las reglas de «Corregir».
  const sorted = [...segments].sort(
    (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
  );
  const index = sorted.findIndex((s) => s.id === segment.id);
  const ids = plan.pieces.map((p, i) =>
    p.isOriginal ? segment.id : `planned-${i}`
  );
  const withPieces = [
    ...sorted.filter((s) => s.id !== segment.id),
    ...plan.pieces.map((p, i) => pieceAsSegment(p, ids[i], segment)),
  ];
  for (const [i, piece] of plan.pieces.entries()) {
    const error = validateSegmentCorrection(withPieces, ids[i], {
      pkInitKm: piece.pkInit.km,
      pkInitMeter: piece.pkInit.meter,
      pkEndKm: piece.pkEnd.km,
      pkEndMeter: piece.pkEnd.meter,
      direction: piece.direction,
      startDate: piece.startDate.toISOString(),
      endDate: piece.endDate.toISOString(),
      roadId: piece.roadId,
      roadStretches: piece.roadStretches,
    });
    if (error) {
      // Una pieza que no cuadra es casi siempre la que sigue a una parada en la
      // que giraron: se señala la parada tras la que empieza; si no hay
      // ninguna (la primera pieza), la parada en la que acaba.
      const at = (iso: string) => new Date(iso).getTime();
      const culprit =
        enabled.find((d) => at(d.endDate) === piece.startDate.getTime()) ??
        enabled.find((d) => at(d.startDate) === piece.endDate.getTime());
      return { ...empty, error, errorDraftKey: culprit?.key ?? null };
    }
  }

  const rows: PreviewRow[] = [];
  plan.pieces.forEach((piece, i) => {
    if (i > 0) {
      const gap =
        piece.startDate.getTime() - plan.pieces[i - 1].endDate.getTime();
      rows.push({ kind: 'stop', minutes: Math.round(gap / 60_000) });
    }
    rows.push({
      kind: 'piece',
      number: (index >= 0 ? index : 0) + i + 1,
      from: pkFormatter(piece.pkInit.km, piece.pkInit.meter),
      to: pkFormatter(piece.pkEnd.km, piece.pkEnd.meter),
      start: hm(piece.startDate),
      end: hm(piece.endDate),
      km: formatSegmentKm(
        piece.pkInit.km,
        piece.pkInit.meter,
        piece.pkEnd.km,
        piece.pkEnd.meter,
        piece.roadStretches
      ),
      pauseCause: piece.pauseCause,
      pauseCauseDetail: piece.pauseCauseDetail,
      isOriginal: piece.isOriginal,
    });
  });

  // La parada que cierra una pieza es la que empieza justo cuando la pieza
  // acaba: la pieza toma su causa.
  const closingStop = (pieceEnd: Date) =>
    enabled.find((d) => new Date(d.startDate).getTime() === pieceEnd.getTime());

  const missingCauseIds: string[] = [];
  const pieces: RouteSegment[] = plan.pieces.map((piece, i) => {
    if (piece.isOriginal) {
      return {
        ...pieceAsSegment(piece, ids[i], segment),
        pauseCause: piece.pauseCause ?? null,
        pauseCauseDetail: piece.pauseCauseDetail ?? null,
      };
    }
    // Una pieza nueva no hereda los vínculos ni las marcas del original. Si
    // pasa a aviso, enseña la OT que el servidor le ligará —la misma regla que
    // «Se vinculará a…»: una sola candidata— o queda pendiente (29-oct).
    const stop = closingStop(piece.endDate);
    const lacksCause = !!stop && missingCause.includes(stop);
    if (lacksCause) missingCauseIds.push(ids[i]);
    const isAviso = !lacksCause && piece.pauseCause === 'external-call';
    const willLink =
      isAviso && stop?.candidatesCount === 1 && stop.candidateId
        ? {
            id: stop.candidateId,
            roid: stop.candidateRoid,
            type: stop.candidateType,
          }
        : null;
    return {
      ...pieceAsSegment(piece, ids[i], segment),
      pauseCause: lacksCause ? null : (piece.pauseCause ?? null),
      pauseCauseDetail: piece.pauseCauseDetail ?? null,
      linkedBasicActionOrder: null,
      linkedExternalOrder: willLink,
      stopSplit: null,
      transfer: null,
      pendingActionLink: false,
      pendingExternalLink: isAviso && !willLink,
    };
  });

  // Los vecinos reales (sin tocar) y la nota de cada fila de la vista previa.
  const before = index > 0 ? sorted[index - 1] : null;
  const after =
    index >= 0 && index < sorted.length - 1 ? sorted[index + 1] : null;
  // Las paradas, como filas propias, colocadas por su hora.
  const stopSegments = enabled.map((d) => stopAsSegment(d, segment));
  const ordered = [
    ...(before ? [before] : []),
    ...pieces,
    ...stopSegments,
    ...(after ? [after] : []),
  ].sort(
    (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
  );
  // La fila de una parada es el hueco que quedará entre el tramo de antes y el
  // de después (29-oct, el usuario: «no entiendo el PK de la parada»): sus
  // horas y sus PK, al minuto que se ve. En medio es la parada tal cual (mismo
  // PK); la de «al inicio» va desde que acabó el tramo de antes, porque esos
  // minutos pasan a su pausa.
  const toMinute = (iso: string) => {
    const date = new Date(iso);
    date.setSeconds(0, 0);
    return date.toISOString();
  };
  const withStopRows: RouteSegment[] = ordered.map((row, i): RouteSegment => {
    if (rowKindIsStop(row.id)) {
      const prev = ordered[i - 1];
      const next = ordered[i + 1];
      return {
        ...row,
        startDate: toMinute(prev?.endDate ?? row.startDate),
        endDate: toMinute(next?.startDate ?? row.endDate ?? row.startDate),
        pkInitKm: prev?.pkEndKm ?? row.pkInitKm,
        pkInitMeter: prev?.pkEndMeter ?? row.pkInitMeter,
        pkEndKm: next?.pkInitKm ?? row.pkEndKm,
        pkEndMeter: next?.pkInitMeter ?? row.pkEndMeter,
      };
    }
    return row;
  });
  const ownIndex = index >= 0 ? index : 0;
  // El árbol (30-oct): el tramo de hoy de padre y, debajo, las piezas y las
  // paradas en su orden. Cada parada cuelga del tramo cuya pausa es: la de en
  // medio, de la pieza que cierra; la del inicio, del tramo de ANTES, porque
  // sus minutos pasan a su pausa (el usuario: colgada del partido «no se
  // entiende que esos minutos pasen al tramo anterior»). Sin tramo de antes
  // (primero del recorrido) no es pausa de nadie: cuelga del padre.
  const parent: RouteSegment = { ...segment, id: `parent-${segment.id}` };
  const between = withStopRows.filter(
    (row) => row.id !== before?.id && row.id !== after?.id
  );
  const leading =
    before && between[0] && rowKindIsStop(between[0].id) ? between[0] : null;
  const children = leading ? between.slice(1) : between;
  const lastPiece = children.reduce(
    (last, row, i) => (rowKindIsStop(row.id) ? last : i),
    -1
  );
  const draftOf = (rowId: string) =>
    enabled.find((d) => `stop-${d.key}` === rowId) as StopDraft;
  const rowNotes: Record<string, PreviewRowNote> = {};
  if (before) rowNotes[before.id] = { kind: 'context' };
  if (leading) {
    const draft = draftOf(leading.id);
    rowNotes[leading.id] = {
      kind: 'stop',
      depth: 1,
      last: true,
      edge: 'start',
      pauseOf: ownIndex,
      stoppedMinutes: minutesOf(draft.startDate, draft.endDate),
      extraMinutes:
        minuteOf(new Date(draft.startDate).getTime()) -
        minuteOf(new Date(segment.startDate).getTime()),
    };
  }
  rowNotes[parent.id] = { kind: 'parent', number: ownIndex + 1 };
  children.forEach((row, i) => {
    if (!rowKindIsStop(row.id)) {
      rowNotes[row.id] =
        i === lastPiece
          ? { kind: 'piece', depth: 1, last: true }
          : { kind: 'piece', depth: 1 };
      return;
    }
    const draft = draftOf(row.id);
    if (i === 0) {
      rowNotes[row.id] = {
        kind: 'stop',
        depth: 1,
        edge: 'start',
        shiftEdge: true,
        extraMinutes:
          minuteOf(new Date(draft.startDate).getTime()) -
          minuteOf(new Date(segment.startDate).getTime()),
      };
    } else if (i > lastPiece) {
      rowNotes[row.id] = {
        kind: 'stop',
        depth: 2,
        through: false,
        edge: 'end',
        // La pausa es de la última pieza, nunca del tramo de después.
        ...(after
          ? {
              pauseOf: ownIndex + plan.pieces.length,
              stoppedMinutes: minutesOf(draft.startDate, draft.endDate),
            }
          : { shiftEdge: true }),
        extraMinutes:
          minuteOf(new Date(segment.endDate as string).getTime()) -
          minuteOf(new Date(draft.endDate).getTime()),
      };
    } else {
      rowNotes[row.id] = { kind: 'stop', depth: 2, through: true };
    }
  });
  if (after) rowNotes[after.id] = { kind: 'context', wasNumber: ownIndex + 2 };
  const previewSegments = [
    ...(before ? [before] : []),
    ...(leading ? [leading] : []),
    parent,
    ...children,
    ...(after ? [after] : []),
  ];

  return {
    // Con la causa provisional el plan es válido, pero no se puede guardar.
    error: missingCause.length > 0 ? CAUSE_REQUIRED : null,
    errorDraftKey: missingCause[0]?.key ?? null,
    rows,
    pieces,
    firstNumber: (index >= 0 ? index : 0) + 1,
    shortensShift:
      (plan.startMoved && index === 0) ||
      (plan.endMoved && index === sorted.length - 1),
    causeMissing: missingCause.length > 0,
    missingCauseIds,
    previewSegments,
    previewFirstNumber: before ? index : ownIndex + 1,
    rowNotes,
  };
}

/**
 * Lo que viaja al servidor: solo las paradas marcadas.
 *
 * `directionLabel` da el nombre del sentido como lo guarda el cronómetro al
 * abrir un tramo (`t(directionNameKey(direction))`): si giraron en la parada,
 * la pieza que sigue nace con el nombre de su sentido nuevo, igual que un
 * tramo abierto así. Sin giro no se manda nada y conserva el suyo.
 */
export function draftsToInputs(
  drafts: StopDraft[],
  directionLabel: (direction: string) => string
): SegmentStopInput[] {
  return drafts
    .filter((d) => d.enabled)
    .map((d) => {
      const pk = parsePk(d.pk) as { km: number; meter: number };
      return {
        startDate: d.startDate,
        endDate: d.endDate,
        pkKm: pk.km,
        pkMeter: pk.meter,
        roadId: d.roadId,
        latitude: d.latitude,
        longitude: d.longitude,
        pauseCause: d.pauseCause || null,
        pauseCauseDetail: d.pauseCauseDetail,
        directionAfter: d.directionAfter,
        directionNameAfter: d.directionAfter
          ? directionLabel(d.directionAfter)
          : null,
      };
    });
}
